import React, { useEffect } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
} from "@mui/material";

import { useParams } from "react-router-dom";
import ScheduleComponent from "./ScheduleComponent";
import { formatMrqNumbersFromTask } from "../../MCS3/Schedule4D/scheduleTaskApi";
import { normalizeSi } from "../../MCS3/Schedule4D/siUtils";
import {
  isDescendantSubtaskOf,
  isWbsSubtaskCode,
  parseTaskCode,
} from "../../../../Project/plant/PlantTaskPage/TaskList/components/taskCodeUtils";

const normalizeTaskMembers = (raw) => {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === "string" && raw.trim()) {
    return raw.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const SubTaskModal = ({
  open,
  onClose,
  mainTask,
  allTasks = [],
  accessToken,
  onRefresh,
  plantPeople = [],
  plantLeads = [],
  resources = [],
  onSiUpdated,
}) => {
  const { id: projectId } = useParams();
  const [rows, setRows] = React.useState([]);

  useEffect(() => {
    if (!open || !mainTask || !Array.isArray(allTasks)) {
      setRows([]);
      return;
    }

    let filteredTasks;

    if (mainTask._isGroup) {
      const targetGroup = mainTask._groupName;
      filteredTasks = allTasks.filter((t) => {
        const { groupName } = parseTaskCode(t.taskCode || "");
        if (groupName !== targetGroup) return false;
        return isWbsSubtaskCode(t.taskCode);
      });
    } else {
      const { groupName: mainGroup } = parseTaskCode(mainTask.taskCode || "");
      filteredTasks = allTasks.filter((t) => {
        const { groupName } = parseTaskCode(t.taskCode || "");
        if (groupName !== mainGroup) return false;
        return isDescendantSubtaskOf(mainTask.taskCode, t.taskCode);
      });
    }

    const getDatePart = (value) => {
      if (!value) return "";
      return typeof value === "string" ? value.slice(0, 10) : "";
    };
    const withCodes = filteredTasks.map((task) => {
      const type = (task.type || "").toLowerCase();
      let fourD = "";
      if (type === "engineering") fourD = "EMS";
      else if (type === "procurement") fourD = "PMS";
      else if (type === "fabrication") fourD = "CMS";
      else if (type === "itp") fourD = "ITP";

      const { code } = parseTaskCode(task.taskCode || "");
      const mrqNumber = formatMrqNumbersFromTask(task);

      return {
        id: task.id,
        uid:
          parseTaskCode(task.taskCode || "").uid || task.uid || "",
        task: task.task || task.name || "",
        name: task.task || task.name || "",
        type: task.type || null,
        taskCode: task.taskCode,
        fourD,
        wbsId: code || task.taskCode || "",
        mrqNumber,
        si: normalizeSi(task.si),
        plannedStart: getDatePart(task.startDate),
        plannedEnd: getDatePart(task.endDate),
        revisedStart: getDatePart(task.revisedStart),
        revisedEnd: getDatePart(task.revisedEnd),
        actualStart: getDatePart(task.actualStart),
        actualEnd: getDatePart(task.actualEnd),
        budgetedHrDollar: task.estimatedTime || 0,
        actualHrDollar: task.manhours ?? 0,
        task_members: normalizeTaskMembers(task.task_members),
        resourceId: task.resourceId ?? task.resource?.id ?? null,
        resource: task.resource ?? null,
      };
    });

    const sorted = withCodes.sort((a, b) => {
      const dateA = a.plannedStart ? new Date(a.plannedStart) : new Date(0);
      const dateB = b.plannedStart ? new Date(b.plannedStart) : new Date(0);
      return dateA - dateB;
    });

    setRows(sorted);
  }, [open, mainTask, allTasks]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        {mainTask?._isGroup
          ? `Tasks in group: ${mainTask._groupName}`
          : `Sub-tasks of ${mainTask?.task || mainTask?.wbsId || "—"} (${mainTask?.wbsId || parseTaskCode(mainTask?.taskCode || "").code})`}
      </DialogTitle>
      <DialogContent>
        {rows && rows.length > 0 ? (
          <Box p={2}>
            <Stack
              sx={{ paddingBottom: "2rem" }}
              direction={"row"}
              justifyContent="space-between"
              height={"3rem"}
              alignItems="center"
            >
              <h2>Schedule</h2>
            </Stack>
            <ScheduleComponent
              rows={rows}
              setRows={setRows}
              includeResponsible
              plantPeople={plantPeople}
              plantLeads={plantLeads}
              projectId={projectId}
              accessToken={accessToken}
              onRefresh={onRefresh}
              resources={resources}
              onSiUpdated={onSiUpdated}
            />
          </Box>
        ) : (
          <Box p={2}>
            <Typography>
              {mainTask?._isGroup
                ? "No tasks in this group."
                : "No sub-tasks for this main task."}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default SubTaskModal;
